import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FormControl, ListGroup, Spinner } from 'react-bootstrap';
import { debounce } from 'lodash';
import UtentiService from '../../services/UtentiService/UtentiService';

const MIN_SEARCH_LEN = 3;

/**
 * Select con ricerca per corsisti (ricerca server-side con debounce).
 */
const SearchUtenteCorsista = ({
  idAgenzia = null,
  selectedUser = null,
  onSelect,
  placeholder = 'Tutti gli utenti',
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [noResults, setNoResults] = useState(false);
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);
  const requestIdRef = useRef(0);

  const selectedLabel = useMemo(() => {
    if (!selectedUser) return '';
    return `${selectedUser.cognome ?? ''} ${selectedUser.nome ?? ''} (${selectedUser.username})`.trim();
  }, [selectedUser]);

  const fetchUsers = async (term, idAg) => {
    const baseFiltri = {
      tipo_utente: 'corsista',
      ...(idAg ? { id_agenzia: String(idAg) } : {}),
    };

    let filtri = { ...baseFiltri };
    if (term.includes('@')) {
      filtri.username = term;
    } else {
      const parts = term.split(/\s+/).filter(Boolean);
      if (parts.length >= 2) {
        filtri.nome = parts[0];
        filtri.cognome = parts.slice(1).join(' ');
      } else {
        filtri.cognome = term;
      }
    }

    const response = await UtentiService.getUtenteByParams({
      filtri,
      limite: 10,
      pagina: 1,
      ordinamento: 'cognome asc',
    });
    const res = await response.json();
    return Array.isArray(res) ? res : [];
  };

  const debouncedSearchRef = useRef(
    debounce(async (term, idAg, reqId) => {
      if (term.length < MIN_SEARCH_LEN) {
        setSearchResults([]);
        setNoResults(false);
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      setNoResults(false);
      try {
        const users = await fetchUsers(term, idAg);
        if (reqId !== requestIdRef.current) return;
        setSearchResults(users);
        setNoResults(users.length === 0);
      } catch {
        if (reqId !== requestIdRef.current) return;
        setSearchResults([]);
        setNoResults(true);
      } finally {
        if (reqId === requestIdRef.current) {
          setIsLoading(false);
        }
      }
    }, 500)
  );

  useEffect(() => {
    if (!isOpen) return undefined;
    const t = setTimeout(() => searchInputRef.current?.focus(), 50);
    return () => clearTimeout(t);
  }, [isOpen]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearchTerm('');
        setSearchResults([]);
        setNoResults(false);
        debouncedSearchRef.current.cancel();
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;
    const reqId = ++requestIdRef.current;
    debouncedSearchRef.current(searchTerm.trim(), idAgenzia, reqId);
    return () => {
      debouncedSearchRef.current.cancel();
    };
  }, [searchTerm, idAgenzia, isOpen]);

  const handleToggle = () => {
    if (disabled) return;
    setIsOpen((open) => {
      if (open) {
        setSearchTerm('');
        setSearchResults([]);
        setNoResults(false);
        debouncedSearchRef.current.cancel();
      }
      return !open;
    });
  };

  const handleSelect = (user) => {
    onSelect(user);
    setIsOpen(false);
    setSearchTerm('');
    setSearchResults([]);
    setNoResults(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    requestIdRef.current += 1;
    debouncedSearchRef.current.cancel();
    onSelect(null);
    setSearchTerm('');
    setSearchResults([]);
    setNoResults(false);
    setIsLoading(false);
    setIsOpen(false);
  };

  return (
    <div className="searchable-select position-relative" ref={containerRef}>
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        className={`form-select searchable-select-control d-flex align-items-center ${disabled ? 'disabled bg-light' : ''}`}
        onClick={handleToggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleToggle();
          }
        }}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className={`flex-grow-1 text-truncate ${!selectedUser ? 'text-muted' : ''}`}>
          {selectedUser ? selectedLabel : placeholder}
        </span>
        <span className="d-flex align-items-center ms-2 flex-shrink-0">
          {selectedUser && !disabled && (
            <i
              className="bi bi-x-lg me-2 text-muted"
              role="button"
              aria-label="Cancella selezione"
              onClick={handleClear}
            />
          )}
          <i className={`bi bi-chevron-${isOpen ? 'up' : 'down'} text-muted`} />
        </span>
      </div>

      {isOpen && !disabled && (
        <div
          className="searchable-select-menu border rounded bg-white shadow-sm mt-1 position-absolute w-100"
          style={{ zIndex: 1060 }}
        >
          <div className="p-2 border-bottom">
            <FormControl
              ref={searchInputRef}
              size="sm"
              type="search"
              placeholder="Cerca per nome, cognome o email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              autoComplete="off"
            />
          </div>

          <ListGroup variant="flush" style={{ maxHeight: 260, overflowY: 'auto' }}>
            <ListGroup.Item
              action
              className="py-2 text-muted"
              onClick={() => handleSelect(null)}
            >
              {placeholder}
            </ListGroup.Item>

            {searchTerm.trim().length < MIN_SEARCH_LEN && (
              <ListGroup.Item disabled className="py-2 small text-muted border-0">
                Digita almeno {MIN_SEARCH_LEN} caratteri per cercare.
              </ListGroup.Item>
            )}

            {isLoading && (
              <ListGroup.Item disabled className="py-2 border-0">
                <Spinner animation="border" size="sm" className="me-2" />
                Ricerca in corso...
              </ListGroup.Item>
            )}

            {!isLoading && noResults && searchTerm.trim().length >= MIN_SEARCH_LEN && (
              <ListGroup.Item disabled className="py-2 small text-muted border-0">
                Nessun corsista trovato.
              </ListGroup.Item>
            )}

            {!isLoading && searchResults.map((user) => (
              <ListGroup.Item
                key={user.id_utente}
                action
                active={selectedUser?.id_utente === user.id_utente}
                className="py-2"
                onClick={() => handleSelect(user)}
              >
                <div className="fw-semibold">{user.cognome} {user.nome}</div>
                <div className="small text-muted">{user.username}</div>
                {user.agenzia && <div className="small text-muted">{user.agenzia}</div>}
              </ListGroup.Item>
            ))}
          </ListGroup>
        </div>
      )}
    </div>
  );
};

export default SearchUtenteCorsista;
